import { isEquivalent } from '../../../utils'

const STATE = {
  canceled: 'canceled',
  inactive: 'inactive',
  polling: 'polling',
  reset: 'reset',
  retriesFailed: 'retriesFailed'
}

const DEFAULT_POLL_INTERVAL = 3 * 1000
const DEFAULT_POLL_BACKOFF = 1.5

export default class Poller {
  constructor(endpointCallback, parameterCallback, freshDataCallback) {
    this.endpointCallback = endpointCallback
    this.parameterCallback = parameterCallback
    this.freshDataCallback = freshDataCallback
    this.state = STATE.inactive
  }

  /**
   * Start polling, no matter what the current state.
   * Performs the first endpoint call immediately.
   */
  startImmediately = (timeout = DEFAULT_POLL_INTERVAL) => {
    this.lastPollTime = timeout
    this.state = STATE.polling
    this.fetch()
  }

  /**
   * Start polling, no matter what the current state.
   * Each call corresponds to one endpoint call.
   * Performs the first endpoint call after DEFAULT_POLL_INTERVAL ms.
   */
  start = (timeout = DEFAULT_POLL_INTERVAL) => {
    this.lastPollTime = timeout
    this.state = STATE.polling
    setTimeout(this.fetch, this.lastPollTime)
  }

  /**
   * Ignore next queued response and stop polling altogether
   */
  cancel = () => {
    this.state = STATE.canceled
  }

  /**
   * Ignore next queued response and restart polling once received
   * Used for ignoring responses for stale parameters
   */
  reset = () => {
    this.lastPollTime = DEFAULT_POLL_INTERVAL
    this.state = STATE.reset
  }

  /**
   * This class increases the polling duration after each request.
   * Call this function to reset the polling duration.
   * Used when the parameters aren't stale (like when the underlying data hasn't changed),
   * but you want to start checking data frequently again.
   */
  resetJustTimer = () => {
    this.lastPollTime = DEFAULT_POLL_INTERVAL
  }

  // For internal use only

  fetch = async (retryCount = 5) => {
    if (this.state === STATE.canceled) {
      return
    }

    if (this.state === STATE.reset) {
      // no dangerous parameters from before reset, continue with fetch
      this.state = STATE.polling
    }

    try {
      const params = this.parameterCallback()
      const res = await this.endpointCallback(params)

      if (isEquivalent(params, this.parameterCallback()) && this.state === STATE.polling) {
        this.freshDataCallback(res.data, () => {
          this.start(this.lastPollTime * DEFAULT_POLL_BACKOFF)
        })
      } else if (this.state === STATE.reset) {
        // begin polling again for new parameters
        this.start()
      }
    } catch (error) {
      if (retryCount > 0) {
        this.fetch(retryCount - 1)
      } else {
        this.state = STATE.retriesFailed
        console.error('Polling Retries Permanently Failed', error)
      }
    }
  }
}
